import React, { useEffect, useMemo, useState } from "react";
import { ChevronDown, Mail, MessageCircle, MessagesSquare, Search } from "lucide-react";
import PageHeader from "components/page-header";
import { Input } from "components/ui/input";
import { Button } from "components/ui/button";
import { Icon } from "components/ui/icon";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "components/ui/dialog";
import { useUserData } from "store/user-data";
import { FAQS, GUIDES, Guide } from "./help-content";

// Support contacts and the chat service come from the environment, so they can change without a code edit.
const WHATSAPP = (process.env.REACT_APP_SUPPORT_WHATSAPP || "").replace(/\D/g, "");
const EMAIL = process.env.REACT_APP_SUPPORT_EMAIL || "";
const TAWK_PROPERTY = process.env.REACT_APP_TAWK_PROPERTY_ID || "";
const TAWK_WIDGET = process.env.REACT_APP_TAWK_WIDGET_ID || "default";
const CRISP_WEBSITE = process.env.REACT_APP_CRISP_WEBSITE_ID || "";
const HAS_CHAT = !!(TAWK_PROPERTY || CRISP_WEBSITE);

declare global {
  interface Window {
    Tawk_API?: any;
    Tawk_LoadStart?: Date;
    $crisp?: any[];
    CRISP_WEBSITE_ID?: string;
  }
}

/** Loads the live chat widget (Tawk.to or Crisp, whichever is configured) once, the first time Help opens. */
function useChatWidget(name?: string, email?: string) {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (!HAS_CHAT) return;
    const id = "storbolt-support-chat";
    const markReady = () => setReady(true);
    // The chat bubble belongs to this page: show it here, hide it again on the way out.
    const hide = () => (CRISP_WEBSITE ? window.$crisp?.push(["do", "chat:hide"]) : window.Tawk_API?.hideWidget?.());
    if (document.getElementById(id)) {
      if (CRISP_WEBSITE) window.$crisp?.push(["do", "chat:show"]);
      else window.Tawk_API?.showWidget?.();
      markReady();
      return hide;
    }
    const script = document.createElement("script");
    script.id = id;
    script.async = true;
    if (CRISP_WEBSITE) {
      window.$crisp = [];
      window.CRISP_WEBSITE_ID = CRISP_WEBSITE;
      if (email) window.$crisp.push(["set", "user:email", [email]]);
      if (name) window.$crisp.push(["set", "user:nickname", [name]]);
      script.src = "https://client.crisp.chat/l.js";
    } else {
      window.Tawk_API = window.Tawk_API || {};
      window.Tawk_LoadStart = new Date();
      window.Tawk_API.visitor = { name, email };
      window.Tawk_API.onLoad = markReady;
      script.src = `https://embed.tawk.to/${encodeURIComponent(TAWK_PROPERTY)}/${encodeURIComponent(TAWK_WIDGET)}`;
      script.charset = "UTF-8";
      script.setAttribute("crossorigin", "*");
    }
    if (CRISP_WEBSITE) script.onload = markReady;
    document.body.appendChild(script);
    return hide;
  }, [name, email]);
  return ready;
}

function openChat() {
  if (CRISP_WEBSITE) window.$crisp?.push(["do", "chat:open"]);
  else window.Tawk_API?.maximize?.();
}

const matches = (term: string, ...texts: string[]) => texts.some((text) => text.toLowerCase().includes(term));

export default function StorboltHelpComponent() {
  const { userData } = useUserData();
  const [query, setQuery] = useState("");
  const [guide, setGuide] = useState<Guide | null>(null);
  const chatReady = useChatWidget(userData?.business_name || userData?.username, userData?.email);

  const term = query.trim().toLowerCase();
  const guides = useMemo(
    () => (term ? GUIDES.filter((g) => matches(term, g.title, g.summary, ...g.steps.flatMap((s) => [s.title, s.body]))) : GUIDES),
    [term],
  );
  const faqs = useMemo(() => (term ? FAQS.filter((f) => matches(term, f.question, f.answer)) : FAQS), [term]);
  const whatsappText = encodeURIComponent(`Hi Storbolt, I need help with my store ${userData?.business_domain || ""}`.trim());

  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Support" title="Help & Support" description="Guides, answers to common questions, and a team that's happy to help." />

      <div className="relative max-w-xl">
        <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-default-400" />
        <Input
          size="lg"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder="Search help, e.g. “payout” or “boost”"
          aria-label="Search help"
          className="pl-11"
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-[1fr_340px]">
        <div className="space-y-6">
          <section>
            <h2 className="mb-3 font-display text-base font-semibold text-default-900">Guides</h2>
            {guides.length === 0 ? (
              <p className="text-sm text-default-500">No guides match “{query}”.</p>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2">
                {guides.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => setGuide(item)}
                    className="flex items-start gap-3 rounded-20px border border-default-200 bg-card p-5 text-start transition-colors hover:border-default-400"
                  >
                    <span className="flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-sage text-sage-foreground">
                      <Icon icon={item.icon} className="h-5 w-5" />
                    </span>
                    <span>
                      <span className="block font-medium text-default-900">{item.title}</span>
                      <span className="mt-0.5 block text-sm text-default-600">{item.summary}</span>
                    </span>
                  </button>
                ))}
              </div>
            )}
          </section>

          <section>
            <h2 className="mb-3 font-display text-base font-semibold text-default-900">Frequently asked questions</h2>
            {faqs.length === 0 ? (
              <p className="text-sm text-default-500">No questions match “{query}”. Try the chat, we're happy to help.</p>
            ) : (
              <div className="divide-y divide-default-200 rounded-20px border border-default-200 bg-card">
                {faqs.map((faq) => (
                  <details key={faq.question} className="group px-5 [&_summary::-webkit-details-marker]:hidden">
                    <summary className="flex cursor-pointer list-none items-center justify-between gap-4 py-4 text-sm font-medium text-default-900">
                      {faq.question}
                      <ChevronDown className="h-4 w-4 flex-none text-default-500 transition-transform group-open:rotate-180" />
                    </summary>
                    <p className="pb-4 text-sm text-default-600">{faq.answer}</p>
                  </details>
                ))}
              </div>
            )}
          </section>
        </div>

        <aside className="h-fit space-y-4 rounded-20px border border-default-200 bg-card p-5 lg:sticky lg:top-24">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-sage text-sage-foreground">
              <MessagesSquare className="h-5 w-5" />
            </span>
            <div>
              <h2 className="font-display text-base font-semibold text-default-900">Live support chat</h2>
              <p className="text-xs text-default-600">Talk to the Storbolt team.</p>
            </div>
          </div>
          <div className="space-y-2">
            {HAS_CHAT && (
              <Button fullWidth disabled={!chatReady} onClick={openChat}>
                <MessagesSquare className="mr-2 h-4 w-4" />
                {chatReady ? "Start a chat" : "Loading chat…"}
              </Button>
            )}
            {WHATSAPP && (
              <Button asChild fullWidth variant="outline">
                <a href={`https://wa.me/${WHATSAPP}?text=${whatsappText}`} target="_blank" rel="noopener noreferrer">
                  <MessageCircle className="mr-2 h-4 w-4" />
                  Chat on WhatsApp
                </a>
              </Button>
            )}
            {EMAIL && (
              <Button asChild fullWidth variant="outline">
                <a href={`mailto:${EMAIL}?subject=${encodeURIComponent(`Help with ${userData?.business_name || "my store"}`)}`}>
                  <Mail className="mr-2 h-4 w-4" />
                  Email support
                </a>
              </Button>
            )}
            {!HAS_CHAT && !WHATSAPP && !EMAIL && (
              <p className="text-sm text-default-600">Support contacts are being set up. Please check back soon.</p>
            )}
          </div>
        </aside>
      </div>

      <Dialog open={!!guide} onOpenChange={(open) => (open ? null : setGuide(null))}>
        <DialogContent className="max-h-[90dvh] overflow-y-auto md:max-w-[560px]">
          {guide && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-xl">{guide.title}</DialogTitle>
                <DialogDescription>{guide.summary}</DialogDescription>
              </DialogHeader>
              <ol className="space-y-4">
                {guide.steps.map((step, index) => (
                  <li key={step.title} className="flex gap-3">
                    <span className="flex h-7 w-7 flex-none items-center justify-center rounded-full bg-default-900 text-xs font-semibold text-default-foreground">
                      {index + 1}
                    </span>
                    <div>
                      <div className="text-sm font-medium text-default-900">{step.title}</div>
                      <p className="mt-0.5 text-sm text-default-600">{step.body}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
